import React from 'react';
import PropTypes from 'prop-types';
import styles from './Button.module.css';

/**
 * Button Component
 * Reusable button with variants: primary, secondary
 * 
 * @param {string} variant - Button variant: 'primary' | 'secondary' | 'deposit' | 'explorePremium'
 * @param {string} type - Button type: 'button' | 'submit' | 'reset'
 * @param {ReactNode} children - Button content
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {boolean} fullWidth - Full width button
 * @param {string} size - Button size: 'sm' | 'md' | 'lg'
 * @param {string} className - Additional CSS classes
 * @param {object} ...props - Other button props
 */
const Button = ({
  variant = 'primary',
  type = 'button',
  children,
  onClick,
  disabled = false,
  fullWidth = false,
  size = 'md',
  className = '',
  ...props
}) => {
  const buttonClasses = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth && styles.fullWidth,
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'deposit', 'explorePremium']),
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="secondary" {...props} />;
export default Button;
